import * as z from 'zod';

const CtaSchema = z.object({ label: z.string(), url: z.string().url() });

const FeatureSchema = z.object({
  id: z.string(),
  title: z.string(),
  cta: CtaSchema.optional(),
  body: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('paragraphs'), paragraphs: z.array(z.string()).min(1) }),
    z.object({
      kind: z.literal('subpoints'),
      subpoints: z.array(z.object({ title: z.string(), description: z.string() })).min(1),
    }),
  ]),
});

const SectionSchema = z.object({ label: z.string(), features: z.array(FeatureSchema).min(1) });

/**
 * Vidéo intégrée (layout `spotlight`). On stocke `provider` + `id` (pas une URL
 * d'embed brute) pour construire la src d'iframe nous-mêmes et garder la main
 * sur les paramètres (autoplay au clic, pas de cookies tiers avant lecture).
 * `youtube` → youtube-nocookie ; `loom`/`vimeo` → embed standard.
 */
const VideoSchema = z.object({
  provider: z.enum(['youtube', 'loom', 'vimeo']),
  id: z.string().min(1), // identifiant de la vidéo chez le provider
  poster: z.string().url().optional(), // vignette affichée avant lecture
});

const IntroCalloutSchema = z.object({
  text: z.string(),
  cta: CtaSchema.optional(),
});

export const AnnouncementSchema = z.object({
  id: z.string(), // slug daté unique = clé du "vu"
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), // ISO 'YYYY-MM-DD' — ordre + filtre joined_at
  // `digest` (défaut) = récap multi-features dépliable (annonce mensuelle).
  // `spotlight` = mise en avant courte ou lancement d'UNE fonctionnalité :
  // intro seule, bénéfices visibles, et vidéo optionnelle.
  layout: z.enum(['digest', 'spotlight']).optional(),
  kicker: z.string(),
  title: z.string(),
  // Libellé court pour la sidebar du centre d'annonces (ex. « L'analyse avancée »).
  // Le `title` éditorial (« Hector s'améliore ») fait un mauvais libellé de nav.
  // Fallback sur `title` si absent.
  navLabel: z.string().optional(),
  subtitle: z.string(),
  intro: z.array(z.string()).optional(), // paragraphes d'accroche (spotlight) ; `**…**` = gras
  introCallout: IntroCalloutSchema.optional(), // incise + CTA entre intro et bénéfices (spotlight)
  video: VideoSchema.optional(), // vidéo intégrée (spotlight)
  // Lien ouvert au clic sur la façade vidéo quand `video` est absent (la vidéo
  // vit sur une page — ex. centre d'aide — plutôt que chez un provider d'embed).
  videoHref: z.string().url().optional(),
  // Note pratique (ex. durée attendue) affichée au-dessus de la vidéo, avant
  // l'invitation `videoCaption`. Ton informatif (gris, non italique).
  videoNote: z.string().optional(),
  videoCaption: z.string().optional(), // invitation centrée (gras italique) au-dessus de la vidéo
  footer: z.object({ text: z.string(), cta: CtaSchema.optional() }).optional(),
  // Certaines annonces spotlight sont volontairement très courtes : header +
  // intro seulement. Les annonces digest gardent des sections avec features.
  sections: z.array(SectionSchema),
}).superRefine((announcement, ctx) => {
  if (announcement.layout !== 'spotlight' && announcement.sections.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Announcement "${announcement.id}" with layout "${announcement.layout ?? 'digest'}" has no sections; add at least one section or use layout "spotlight" for a header-only announcement`,
      path: ['sections'],
    });
  }
});

export type Announcement = z.infer<typeof AnnouncementSchema>;
export type AnnouncementFeature = z.infer<typeof FeatureSchema>;
